import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createCustomerDebtReportLink,
  downloadCustomerDebtReport,
  getCustomerContacts,
  getCustomerDebtReport,
  getCustomerOpenInvoices,
  getSettings,
  getDebtReportRecipients,
  logCustomerInteraction,
  renderMessageTemplate,
  sendDebtReportToMembers,
  sendDebtMessageSms,
  type CustomerContact,
  type CustomerOpenInvoice,
  type DebtReportLinkResponse,
  type DebtReportMemberSendResponse,
  type DebtReportInternalRecipient,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { summarizeOpenInvoicesByScope } from '../lib/openBalanceDebtScope';
import { normalizeIsraeliPhoneNumber, normalizePhoneForWhatsAppLink } from '../lib/phone';
import type { CustomerDebtDetail } from './CustomerDetailsModal';
import EditMessageTemplateModal from './EditMessageTemplateModal';
import PaymentLinkWizardModal from './PaymentLinkWizardModal';
import { ReconciliationWarning } from './CustomerReconciliationPanel';

interface DebtReportSendModalProps {
  customerId: number;
  customerName: string;
  phone?: string | null;
  email?: string | null;
  onClose: () => void;
  onInteractionLogged?: () => void;
}

type DeliveryChannel = 'whatsapp' | 'email' | 'sms';

function formatNIS(amount: number): string {
  return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);
}

function validNormalizedPhone(value?: string | null): string {
  const normalized = normalizeIsraeliPhoneNumber(value);
  return /^\+[1-9]\d{7,14}$/.test(normalized) ? normalized : '';
}

function memberDisplayName(member: DebtReportInternalRecipient): string {
  return member.name || member.user_id;
}

function memberRoleLabel(member: DebtReportInternalRecipient, t: TFunction): string {
  if (member.role === 'account_executive') return 'AE';
  if (member.role === 'owner') return t('debtReportWhatsApp.ownerRole', 'Owner');
  if (member.role === 'admin') return t('debtReportWhatsApp.adminRole', 'Admin');
  return t('debtReportWhatsApp.memberRole', 'Member');
}

function openEmailDraft(recipient: string, subject: string, body: string): void {
  const link = document.createElement('a');
  link.href = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Generates one private report link per modal session. Customer delivery uses
// the selected device-local handoff; workspace recipients remain WhatsApp-only
// and are delivered through the approved Twilio Content template.
export default function DebtReportSendModal({
  customerId,
  customerName,
  phone,
  email,
  onClose,
  onInteractionLogged,
}: DebtReportSendModalProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language?.startsWith('he') ? 'he' : 'en';

  const [channel, setChannel] = useState<DeliveryChannel>('whatsapp');
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [contactId, setContactId] = useState('');
  const [contactsError, setContactsError] = useState(false);
  const [pendingHistory, setPendingHistory] = useState<{
    channel: 'whatsapp' | 'email'; destination: string; contact_id?: string; contact_name?: string; contact_role?: string; message_text: string; request_id: string;
  } | null>(null);
  const [smsAttempt, setSmsAttempt] = useState<{ message_text: string; request_id: string } | null>(null);
  const [smsAccepted, setSmsAccepted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(phone || '');
  const [emailAddress, setEmailAddress] = useState(email || '');
  const [includeCustomer, setIncludeCustomer] = useState(Boolean(validNormalizedPhone(phone)));
  const [members, setMembers] = useState<DebtReportInternalRecipient[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [businessName, setBusinessName] = useState('קפה דאדא');
  const [bankTransferDetails, setBankTransferDetails] = useState('');
  const [settingsError, setSettingsError] = useState(false);
  const [settingsRevision, setSettingsRevision] = useState(0);
  const [paymentLinksEnabled, setPaymentLinksEnabled] = useState(false);
  const [report, setReport] = useState<DebtReportLinkResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportAttempt, setReportAttempt] = useState(0);
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState<CustomerDebtDetail | null>(null);
  const [openInvoices, setOpenInvoices] = useState<CustomerOpenInvoice[] | null>(null);
  const [debtDataError, setDebtDataError] = useState<string | null>(null);
  const [includeMonthlyDetails, setIncludeMonthlyDetails] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [showPaymentLinkWizard, setShowPaymentLinkWizard] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [templateRevision, setTemplateRevision] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [deliveryResult, setDeliveryResult] = useState<DebtReportMemberSendResponse | null>(null);
  const compositionLocked = sending || Boolean(pendingHistory || smsAttempt);
  const previewMessage = smsAttempt?.message_text ?? pendingHistory?.message_text ?? message;

  useEffect(() => {
    let active = true;
    getCustomerContacts(customerId).then(response => { if (active) setContacts(response.data.contacts); })
      .catch(() => { if (active) setContactsError(true); });
    return () => { active = false; };
  }, [customerId]);

  useEffect(() => {
    let active = true;
    setSettingsError(false);
    getSettings()
      .then((response) => {
        const data = response.data?.settings ?? response.data ?? {};
        if (active) {
          setBusinessName(data.business_name || data.customer_bot_business_name || 'קפה דאדא');
          setBankTransferDetails(String(data.bank_transfer_details || '').trim());
          setPaymentLinksEnabled(data.payment_links_enabled === true);
        }
      })
      .catch(() => {
        if (active) setSettingsError(true);
      });
    return () => { active = false; };
  }, [settingsRevision]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setDebtDataError(null);
    Promise.all([getCustomerDebtReport(customerId), getCustomerOpenInvoices(customerId)])
      .then(([detailResponse, invoicesResponse]) => {
        if (!active) return;
        setDetail(detailResponse.data);
        setOpenInvoices(Array.isArray(invoicesResponse.data) ? invoicesResponse.data : []);
        setEmailAddress((current) => current || detailResponse.data.email || '');
      })
      .catch((err) => {
        if (!active) return;
        setDetail(null);
        setOpenInvoices(null);
        setDebtDataError(
          err?.response?.data?.detail
          || t('debtReportSend.debtDataError', 'Debt details could not be loaded.'),
        );
      });
    return () => { active = false; };
  }, [customerId, t]);

  useEffect(() => {
    if (!user?.tenant_id) return;
    let active = true;
    setMembersLoading(true);
    setMembersError(null);
    getDebtReportRecipients(customerId)
      .then((response) => {
        if (active) {
          setMembers(response.data);
          const availableIds = new Set(response.data.filter((member) => member.eligible !== false).map((member) => member.user_id));
          setSelectedMemberIds((current) => new Set([...current].filter((id) => availableIds.has(id))));
        }
      })
      .catch(() => {
        if (active) setMembersError(t('debtReportWhatsApp.membersLoadError', 'Failed to load workspace users.'));
      })
      .finally(() => {
        if (active) setMembersLoading(false);
      });
    return () => { active = false; };
  }, [customerId, t, user?.tenant_id]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setReportLoading(true);
    setReportError(null);
    setReport(null);
    createCustomerDebtReportLink(customerId, { locale })
      .then((response) => {
        if (active) setReport(response.data);
      })
      .catch((err) => {
        if (active) {
          setReportError(
            err?.response?.data?.detail
            || t('debtReportWhatsApp.reportError', 'Failed to generate the debt report.'),
          );
        }
      })
      .finally(() => {
        if (active) setReportLoading(false);
      });
    return () => { active = false; };
  }, [customerId, locale, reportAttempt, t]);

  const eligibleMembers = useMemo(
    () => members.filter((member) => member.eligible !== false),
    [members],
  );
  const assignedAes = useMemo(
    () => eligibleMembers.filter(
      (member) => member.role === 'account_executive'
        && member.assigned_to_customer,
    ),
    [customerId, eligibleMembers],
  );
  const workspaceOwners = useMemo(
    () => eligibleMembers.filter((member) => member.role === 'owner'),
    [eligibleMembers],
  );
  const normalizedPhone = validNormalizedPhone(phoneNumber);
  const normalizedEmail = emailAddress.trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const customerReady = includeCustomer && (
    channel === 'email' ? validEmail : Boolean(channel === 'sms' ? validNormalizedPhone(phone) : normalizedPhone)
  );

  const toggleMember = useCallback((userId: string) => {
    setDeliveryResult(null);
    setSelectedMemberIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((group: DebtReportInternalRecipient[]) => {
    const ids = group.map((member) => member.user_id);
    if (!ids.length) return;
    setDeliveryResult(null);
    setSelectedMemberIds((current) => {
      const next = new Set(current);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  }, []);

  const isGroupSelected = useCallback(
    (group: DebtReportInternalRecipient[]) => group.length > 0 && group.every((member) => selectedMemberIds.has(member.user_id)),
    [selectedMemberIds],
  );

  const selectedMembersForDelivery = useMemo(() => {
    if (channel !== 'whatsapp') return [];
    return eligibleMembers.filter((member) => selectedMemberIds.has(member.user_id));
  }, [channel, eligibleMembers, selectedMemberIds]);
  const internalRecipientsReady = selectedMembersForDelivery.length > 0;
  const customerDuplicateCount = selectedMemberIds.size - selectedMembersForDelivery.length;

  const expiresAtLabel = useMemo(() => {
    if (!report?.expires_at) return '';
    try {
      return new Date(report.expires_at).toLocaleDateString(locale === 'he' ? 'he-IL' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return report.expires_at;
    }
  }, [locale, report?.expires_at]);

  const scopedSummary = useMemo(
    () => openInvoices && detail
      ? summarizeOpenInvoicesByScope(openInvoices, detail.payment_terms_days ?? 60, 'all')
      : null,
    [detail, openInvoices],
  );
  const agedSummary = useMemo(
    () => openInvoices && detail
      ? summarizeOpenInvoicesByScope(openInvoices, detail.payment_terms_days ?? 60, 'aged')
      : null,
    [detail, openInvoices],
  );
  const monthlyDetails = useMemo(() => {
    if (!includeMonthlyDetails || !scopedSummary) return '';
    const agedMonths = new Set((agedSummary?.months || []).map((month) => month.month));
    const lines = scopedSummary.months.map((month) => {
      const monthLabel = new Date(`${month.month}-01T00:00:00`).toLocaleDateString(
        locale === 'he' ? 'he-IL' : 'en-US',
        { year: 'numeric', month: 'long' },
      );
      return `${agedMonths.has(month.month) ? '⚠️ ' : ''}${monthLabel}: ${formatNIS(month.amount)}`;
    });
    return lines.length
      ? `${t('debtReportSend.monthlyDetailsHeading', 'Debt by month:')}\n${lines.join('\n')}`
      : '';
  }, [agedSummary, includeMonthlyDetails, locale, scopedSummary, t]);

  const templateValues = useMemo<Record<string, string> | null>(() => {
    if (!report) return null;
    const ageDays = detail?.payment_terms_days ?? 60;
    const agedOpen = agedSummary?.total ?? detail?.aged_debt_amount ?? 0;
    return {
      customer_name: report.customer_name || customerName,
      business_name: businessName,
      amount_due: formatNIS(report.total_remaining),
      invoice_count: String(report.invoice_count),
      debt_report_url: report.short_url,
      aged_debt_line: agedOpen > 0
        ? t('debtReportSend.agedDebtLine', 'Of this, debt older than {{days}} days: {{amount}}', {
          days: ageDays,
          amount: formatNIS(agedOpen),
        })
        : '',
      monthly_details: monthlyDetails,
      payment_link_line: paymentLink
        ? t('debtReportSend.paymentLinkLine', 'Secure payment: {{url}}', { url: paymentLink })
        : '',
      payment_link: paymentLink || '',
      bank_transfer_details: bankTransferDetails,
      bank_transfer_line: bankTransferDetails
        ? t('debtReportSend.bankTransferLine', 'Bank transfer details:\n{{details}}', {
          details: bankTransferDetails,
        })
        : '',
    };
  }, [agedSummary, bankTransferDetails, businessName, customerName, detail, monthlyDetails, paymentLink, report, t]);

  useEffect(() => {
    if (!templateValues) {
      setMessage('');
      return;
    }
    let active = true;
    setTemplateError(null);
    renderMessageTemplate('debt_report_message', templateValues)
      .then((response) => {
        if (active) setMessage(response.data.body);
      })
      .catch((err) => {
        if (active) {
          setMessage('');
          setTemplateError(
            err?.response?.data?.detail?.message
            || err?.response?.data?.detail
            || t('settings.messageTemplates.previewUnavailable', 'Preview unavailable'),
          );
        }
      });
    return () => { active = false; };
  }, [t, templateRevision, templateValues]);

  const handleRetry = useCallback(() => {
    setReportAttempt((current) => current + 1);
  }, []);

  const handlePaymentLinkSuccess = useCallback((url: string) => {
    setPaymentLink(url);
    setShowPaymentLinkWizard(false);
  }, []);

  const handleSend = useCallback(async () => {
    const recipientReady = channel === 'whatsapp'
      ? customerReady || internalRecipientsReady
      : customerReady;
    if (sending || (!pendingHistory && !smsAttempt && (!report || !message || !recipientReady))) return;
    setSending(true);
    setSendError(null);
    setDeliveryResult(null);
    try {
      if (channel === 'sms') {
        const attempt = smsAttempt || { message_text: message, request_id: crypto.randomUUID() };
        setSmsAttempt(attempt);
        const response = await sendDebtMessageSms(customerId, attempt);
        setSmsAccepted(true);
        if (!response.data.history_saved) {
          setSendError(t('collectionWorkflow.smsHistoryError'));
          return;
        }
        onInteractionLogged?.();
        return;
      }

      if (channel === 'email') {
        const attempt = pendingHistory || {
          channel,
          destination: normalizedEmail,
          message_text: message,
          request_id: crypto.randomUUID(),
        };
        if (!pendingHistory) {
          const subject = t('debtReportSend.emailSubject', 'Debt report - {{customer}}', {
            customer: report?.customer_name || customerName,
          });
          openEmailDraft(normalizedEmail, subject, message);
        }
        setPendingHistory(attempt);
        await logCustomerInteraction(customerId, attempt);
        setPendingHistory(null);
        onInteractionLogged?.();
        onClose();
        return;
      }

      const customerAttempt = pendingHistory || (customerReady ? {
        channel: 'whatsapp' as const,
        destination: normalizedPhone,
        contact_id: contactId || undefined,
        contact_name: contactId ? contacts.find(item => item.contact_id === contactId)?.name : undefined,
        contact_role: contactId ? contacts.find(item => item.contact_id === contactId)?.role : undefined,
        message_text: message,
        request_id: crypto.randomUUID(),
      } : null);
      if (customerAttempt && !pendingHistory) {
        const link = document.createElement('a');
        link.href = `https://wa.me/${normalizePhoneForWhatsAppLink(phoneNumber)}?text=${encodeURIComponent(message)}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setPendingHistory(customerAttempt);
      }

      const alreadySent = new Set(
        deliveryResult?.results
          .filter(item => item.status === 'sent')
          .map(item => item.user_id)
        || [],
      );
      const memberIds = selectedMembersForDelivery
        .map(item => item.user_id)
        .filter(id => !alreadySent.has(id));
      const customerRequest = customerAttempt
        ? logCustomerInteraction(customerId, customerAttempt)
        : Promise.resolve(null);
      const memberRequest = memberIds.length > 0 && report
        ? sendDebtReportToMembers(customerId, report.short_code, memberIds, 'attachment')
        : Promise.resolve(null);
      const [customerOutcome, memberOutcome] = await Promise.allSettled([
        customerRequest,
        memberRequest,
      ]);
      const errors: string[] = [];

      if (customerAttempt) {
        if (customerOutcome.status === 'fulfilled') {
          setPendingHistory(null);
          onInteractionLogged?.();
        } else {
          errors.push(t('debtReportSend.historyLogError'));
        }
      }

      if (memberIds.length > 0) {
        if (memberOutcome.status === 'fulfilled' && memberOutcome.value) {
          const result = memberOutcome.value.data;
          setDeliveryResult(result);
          setSelectedMemberIds(current => new Set(
            [...current].filter(id => !result.results.some(item => item.user_id === id && item.status === 'sent')),
          ));
        } else {
          errors.push(t('collectionWorkflow.attachmentError'));
        }
      }

      if (errors.length > 0) {
        setSendError(errors.join(' '));
      } else if (customerAttempt && memberIds.length === 0) {
        onClose();
      }
    } catch (err) {
      const detail = isAxiosError(err) && typeof err.response?.data?.detail === 'string' ? err.response.data.detail : '';
      setSendError(detail || t(channel === 'sms' ? 'collectionWorkflow.smsError' : 'debtReportSend.historyLogError'));
    } finally {
      setSending(false);
    }
  }, [channel, customerId, customerName, customerReady, deliveryResult, internalRecipientsReady, message, normalizedEmail, normalizedPhone, onClose, onInteractionLogged, phoneNumber, report, selectedMembersForDelivery, sending, t, contactId, contacts, pendingHistory, smsAttempt]);

  const recipientReady = channel === 'whatsapp'
    ? customerReady || internalRecipientsReady
    : customerReady;
  const canSend = !reportLoading && !reportError && !!report && !!message && !templateError && !settingsError
    && (Boolean(pendingHistory || smsAttempt) || recipientReady) && !sending && !smsAccepted;

  const handleDownload = async () => {
    if (!report) return;
    try {
      const response = await downloadCustomerDebtReport(customerId, report.short_code);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `debt-report-${customerId}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setSendError(t('collectionWorkflow.downloadError'));
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-sm font-medium text-green-700">{t('debtReportSend.title', 'Send Debt Report')}</p>
            <h2 className="text-lg font-bold text-gray-900">{report?.customer_name || customerName}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('debtReportSend.confirmHint', 'Choose a delivery channel and confirm the message. A fresh, private report link is generated for this send only.')}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50" data-testid="debt-report-send-close">
            {t('common.close', 'Close')}
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-5">
          {reportLoading && (
            <div className="py-6 text-center text-sm text-gray-500" data-testid="debt-report-send-loading">
              {t('debtReportWhatsApp.generatingReport', 'Generating the debt report…')}
            </div>
          )}

          {reportError && !reportLoading && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="debt-report-send-error">
              <p>{reportError}</p>
              <button type="button" onClick={handleRetry} className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">
                {t('debtReportWhatsApp.retry', 'Retry')}
              </button>
            </div>
          )}

          {sendError && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{sendError}</div>}
          {smsAttempt && <p role="status" className="text-sm text-gray-600">{t('collectionWorkflow.smsAttemptLocked')}</p>}
          {settingsError && <div role="alert" className="text-red-700">{t('collectionWorkflow.settingsError')} <button onClick={() => setSettingsRevision(value => value + 1)}>{t('common.retry', 'Retry')}</button></div>}
          {(pendingHistory || (smsAccepted && sendError)) && <button type="button" disabled={sending} onClick={async () => {
            setSmsAccepted(false);
            await handleSend();
          }} className="text-sm text-blue-700 underline">{t('collectionWorkflow.retryHistory')}</button>}
          {smsAccepted && !sendError && <p role="status">{t('collectionWorkflow.accepted')}</p>}
          {debtDataError && <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="alert">{debtDataError}</div>}
          {deliveryResult && (
            <div className={`rounded-md border px-4 py-3 text-sm ${deliveryResult.status === 'sent' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`} role="status" data-testid="debt-report-member-send-result">
              {t('collectionWorkflow.memberAcceptance', 'Internal PDF submission: {{sent}} accepted, {{failed}} failed, {{skipped}} skipped. Acceptance is not confirmed delivery.', {
                sent: deliveryResult.sent_count,
                failed: deliveryResult.failed_count,
                skipped: deliveryResult.skipped_count + customerDuplicateCount,
              })}
            </div>
          )}

          {!reportLoading && !reportError && report && (
            <>
              <div className="grid grid-cols-3 gap-2 rounded-xl bg-gray-100 p-1" data-testid="debt-report-channel-selector">
                {(['whatsapp', 'email', 'sms'] as DeliveryChannel[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    disabled={compositionLocked}
                    onClick={() => {
                      setChannel(value);
                      setIncludeCustomer(
                        value === 'email' ? validEmail : Boolean(validNormalizedPhone(value === 'sms' ? phone : phoneNumber)),
                      );
                      setDeliveryResult(null);
                      setSendError(null);
                    }}
                    aria-pressed={channel === value}
                    className={`rounded-lg px-3 py-2 text-sm font-medium ${channel === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    data-testid={`debt-report-channel-${value}`}
                  >
                    {value === 'sms' ? 'SMS' : value === 'whatsapp'
                      ? t('debtReportSend.whatsAppChannel', 'WhatsApp')
                      : t('debtReportSend.emailChannel', 'Email')}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs font-medium text-amber-700">{t('debtReportWhatsApp.totalOpen', 'Total open debt')}</div>
                  <div className="mt-1 text-lg font-bold text-amber-900">{formatNIS(report.total_remaining)}</div>
                </div>
                <ReconciliationWarning data={report.reconciliation} />
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-medium text-gray-600">{t('debtReportWhatsApp.invoiceCount', { count: report.invoice_count })}</div>
                  <div className="mt-1 text-lg font-bold text-gray-900">{report.invoice_count}</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-medium text-gray-600">{t('debtReportWhatsApp.expiryLabel', 'Expiry')}</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">{t('debtReportWhatsApp.expiresIn', 'Link valid for 3 days (until {{date}})', { date: expiresAtLabel })}</div>
                </div>
              </div>
              {report.excluded_unknown_count > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="debt-report-balance-warning">
                  {t(
                    'invoiceReport.uncertainBalanceWarning',
                    '{{count}} invoice balance could not be verified and is excluded from report totals.',
                    { count: report.excluded_unknown_count },
                  )}
                </div>
              )}

              <section className="space-y-3 rounded-xl border border-gray-200 p-4" data-testid="debt-report-recipient-picker">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{t('debtReportWhatsApp.recipientsTitle', 'Recipients')}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{t('debtReportWhatsApp.recipientsHint', 'The customer opens on this device. Workspace users receive the report through Twilio.')}</p>
                  {channel === 'whatsapp' && <p className="mt-1 text-xs text-gray-500">{t('debtReportWhatsApp.eligibilityHint')}</p>}
                </div>

                <div className={`rounded-lg border p-3 ${includeCustomer ? 'border-green-300 bg-green-50/50' : 'border-gray-200 bg-gray-50'}`}>
                  <label className="flex cursor-pointer items-center gap-3">
                    <input type="checkbox" checked={includeCustomer} disabled={sending || Boolean(pendingHistory || smsAttempt)} onChange={(event) => { setIncludeCustomer(event.target.checked); setDeliveryResult(null); }} className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" data-testid="debt-report-customer-recipient" />
                    <span className="text-sm font-medium text-gray-900">{t('debtReportWhatsApp.customerRecipient', 'Customer')}</span>
                    <span className="text-xs text-gray-500">{report.customer_name || customerName}</span>
                  </label>
                  <div className="mt-2 ps-7">
                    {channel === 'whatsapp' && <>
                      {contactsError && <p role="alert" className="text-red-700">{t('collectionWorkflow.contactsError')}</p>}
                      <select aria-label={t('collectionWorkflow.selectContact')} value={contactId} disabled={sending || Boolean(pendingHistory)} className="mb-2 w-full rounded border p-2"
                        onChange={event => {
                          setContactId(event.target.value);
                          const destination = contacts.find(item => item.contact_id === event.target.value)?.phone || phone || '';
                          setPhoneNumber(destination);
                          setIncludeCustomer(Boolean(validNormalizedPhone(destination)));
                        }}>
                        <option value="">{t('collectionWorkflow.primaryPhone')}</option>
                        {contacts.map(contact => <option key={contact.contact_id} value={contact.contact_id}>{contact.name} {contact.role} - {contact.phone}</option>)}
                      </select>
                    </>}
                    {channel === 'sms' && <p className="text-xs text-gray-500">{t('collectionWorkflow.smsPrimaryOnly')}</p>}
                    <input
                      type={channel === 'email' ? 'email' : 'tel'}
                      value={channel === 'sms' ? phone || '' : channel === 'whatsapp' ? phoneNumber : emailAddress}
                      disabled={sending || channel === 'sms' || Boolean(pendingHistory)}
                      onChange={(event) => {
                        if (channel === 'whatsapp') { setPhoneNumber(event.target.value); setContactId(''); }
                        else setEmailAddress(event.target.value);
                        setDeliveryResult(null);
                      }}
                      placeholder={channel === 'whatsapp'
                        ? t('debtReportWhatsApp.whatsAppNumberPlaceholder', 'e.g. 050-1234567')
                        : t('debtReportSend.emailPlaceholder', 'customer@example.com')}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                      data-testid="debt-report-customer-destination"
                    />
                    <span className={`mt-1 block text-xs ${includeCustomer && (channel === 'email' ? !validEmail : !validNormalizedPhone(channel === 'sms' ? phone : phoneNumber)) ? 'text-red-600' : 'text-gray-500'}`}>
                      {channel === 'sms' ? t('collectionWorkflow.smsPrimaryOnly') : channel === 'whatsapp'
                        ? (normalizedPhone
                          ? t('debtReportWhatsApp.normalizedWhatsAppNumber', 'Will send to {{phone}}', { phone: normalizedPhone })
                          : t('debtReportWhatsApp.noPhoneNumber', 'No valid phone number on file'))
                        : (validEmail
                          ? t('debtReportSend.emailReady', 'Will open an email to {{email}}', { email: normalizedEmail })
                          : t('debtReportSend.noEmail', 'Enter a valid email address'))}
                    </span>
                  </div>
                </div>

                {channel === 'whatsapp' && <div className="flex flex-wrap gap-2" aria-label={t('debtReportWhatsApp.quickSelect', 'Quick select workspace users')}>
                  {[
                    { key: 'assigned-aes', label: t('debtReportWhatsApp.assignedAes', 'Assigned AE(s)'), members: assignedAes },
                    { key: 'workspace-owner', label: t('debtReportWhatsApp.workspaceOwner', 'Workspace owner'), members: workspaceOwners },
                    { key: 'all-users', label: t('debtReportWhatsApp.allUsers', 'All users'), members: eligibleMembers },
                  ].map((group) => (
                    <button key={group.key} type="button" onClick={() => toggleGroup(group.members)} disabled={sending || group.members.length === 0} aria-pressed={isGroupSelected(group.members)} data-testid={`debt-report-${group.key}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40 ${isGroupSelected(group.members) ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                      {group.label} ({group.members.length})
                    </button>
                  ))}
                </div>}

                {channel === 'whatsapp' && (membersLoading ? (
                  <p className="py-3 text-center text-sm text-gray-500">{t('debtReportWhatsApp.loadingMembers', 'Loading workspace users…')}</p>
                ) : membersError ? (
                  <p className="text-sm text-red-600" role="alert">{membersError}</p>
                ) : members.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('debtReportWhatsApp.noEligibleMembers')}</p>
                ) : (
                  <div className="max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200" data-testid="debt-report-member-list">
                    {members.map((member) => (
                      <label key={member.user_id} className={`flex items-center gap-3 px-3 py-2.5 ${member.eligible === false ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}>
                        <input type="checkbox" checked={member.eligible !== false && selectedMemberIds.has(member.user_id)} disabled={member.eligible === false || sending} aria-describedby={member.eligible === false ? `debt-report-member-reason-${member.user_id}` : undefined} onChange={() => toggleMember(member.user_id)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid={`debt-report-member-${member.user_id}`} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-900">{memberDisplayName(member)}</span>
                          {member.assigned_to_customer && <span className="block text-xs text-blue-700">{t('debtReportWhatsApp.assignedToCustomer')}</span>}
                          {member.eligible === false ? (
                            <span id={`debt-report-member-reason-${member.user_id}`} className="block text-xs text-amber-800">
                              {t(`debtReportWhatsApp.blockedReasons.${member.blocked_reason || 'unavailable'}`)}
                            </span>
                          ) : <span className="block text-xs text-gray-500">{member.phone_hint}</span>}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{memberRoleLabel(member, t)}</span>
                      </label>
                    ))}
                  </div>
                ))}

                {channel === 'whatsapp' && customerDuplicateCount > 0 && customerReady && (
                  <p className="text-xs text-amber-700">{t('debtReportWhatsApp.duplicateCustomerPhone', '{{count}} selected workspace recipient shares the customer phone and will not receive a duplicate message.', { count: customerDuplicateCount })}</p>
                )}
              </section>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-gray-700">{t('debtReportWhatsApp.messagePreview', 'Message preview')}</div>
                  {templateValues && !compositionLocked && <EditMessageTemplateModal templateKey="debt_report_message" values={templateValues} onChanged={() => setTemplateRevision((current) => current + 1)} />}
                </div>
                {templateError && <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700" role="alert">{templateError}</div>}
                <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-800" data-testid="debt-report-message-preview">
                  {previewMessage || t('settings.messageTemplates.previewUnavailable', 'Preview unavailable')}
                </div>
                {channel === 'whatsapp' && selectedMemberIds.size > 0 && (
                  <p className="mt-1 text-xs text-gray-500">{t('debtReportWhatsApp.standardTemplateNote', 'Workspace users receive the approved standard version of this message.')}</p>
                )}
              </div>

              <div className="space-y-3 rounded-xl border border-gray-200 p-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={includeMonthlyDetails}
                    onChange={(event) => setIncludeMonthlyDetails(event.target.checked)}
                    disabled={!scopedSummary || compositionLocked}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="debt-report-include-monthly"
                  />
                  <span className="text-sm font-medium text-gray-900">{t('debtReportSend.includeMonthlyDetails', 'Include monthly debt details')}</span>
                </label>
                {paymentLinksEnabled && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={compositionLocked}
                      onClick={() => setShowPaymentLinkWizard(true)}
                      className="rounded-lg border border-green-300 bg-white px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50"
                      data-testid="debt-report-add-payment-link"
                    >
                      {paymentLink
                        ? t('debtReportSend.replacePaymentLink', 'Replace payment link')
                        : t('debtReportSend.addPaymentLink', 'Add payment link')}
                    </button>
                    {paymentLink && (
                      <button
                        type="button"
                        disabled={compositionLocked}
                        onClick={() => setPaymentLink(null)}
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        {t('debtReportSend.removePaymentLink', 'Remove payment link')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="button" onClick={handleSend} disabled={!canSend} data-testid="debt-report-send-button" className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300">
            {sending
              ? t('debtReportSend.sendingButton', 'Sending…')
              : channel === 'sms' ? t('collectionWorkflow.sendSms') : channel === 'whatsapp'
                ? t('debtReportSend.sendWhatsApp', 'Send with WhatsApp')
                : t('debtReportSend.sendEmail', 'Open Email')}
          </button>
          {report && <button type="button" onClick={handleDownload} className="rounded-lg border px-3 py-2">{t('collectionWorkflow.download')}</button>}
        </div>
      </div>
      {showPaymentLinkWizard && (
        <PaymentLinkWizardModal
          customerId={customerId}
          customerName={report?.customer_name || customerName}
          onClose={() => setShowPaymentLinkWizard(false)}
          onSuccess={handlePaymentLinkSuccess}
        />
      )}
    </div>
  );
}
